export const SECTION_NAME = "PurchaseOrder";

export const INTRO_TEXT = `A purchase order is a document requesting that a vendor provide a good or service in exchange for payment.`;

export const EXAMPLE_JSON = `{
    "id": 123,
    "starred": 0,
    "syncToken": 0,
    "number": "IR-12345",
    "date": "2019-05-08T09:13:00",
    "vendor": {
      "id": 23,
      "name": "ABC Plastics"
    },
    "customer": {
      "id": 25,
      "name": "Morrison Machinery"
    },
    "location": {
      "id": 1,
      "name": "Warehouse"
    },
    "billing": {
      "company": "Test Company",
      "contact": null,
      "phone": "+18175551212",
      "email": "email@example.com",
      "addressName": "",
      "addressType": "",
      "address": {
        "line1": "123 Main",
        "line2": null,
        "line3": null,
        "line4": null,
        "line5": null,
        "city": "Syracuse",
        "stateProvince": "NY",
        "postalCode": "13202",
        "country": "US"
      }
    },
    "shipping": {
      "company": "Test Company",
      "contact": null,
      "phone": "+18175551212",
      "email": "email@example.com",
      "addressName": "",
      "addressType": "",
      "address": {
        "line1": "123 Main",
        "line2": null,
        "line3": null,
        "line4": null,
        "line5": null,
        "city": "Syracuse",
        "stateProvince": "NY",
        "postalCode": "13202",
        "country": "US"
      }
    },
    "terms": {
      "id": 11,
      "name": "NET 30"
    },
    "department": null,
    "assignedToUser": null,
    "currency": {
      "id": 42,
      "name": "USD"
    },
    "taxCode": {
      "id": 5,
      "name": "Sales Tax"
    },
    "shippingMethod": {
      "id": 11,
      "name": "FedEx"
    },
    "trackingNumber": "GVZ382398GF899S2",
    "linkedTransaction": null,
    "exchangeRate": 1,
    "vendorMessage": null,
    "comment": null,
    "vendorNotes": "",
    "expectedDate": "2019-06-08T09:13:00",
    "expectedShip": "2019-05-31T09:13:00",
    "customFields": [
      {
        "id": 5,
        "name": "Checkbox",
        "value": "false",
        "dataType": "Boolean"
      },
      {
        "id": 13,
        "name": "Our-Acct-No",
        "value": "ABC84729",
        "dataType": "String"
      }
    ],
    "depositPercent": 0,
    "depositAmount": 0,
    "subTotal": 12,
    "taxAmount": 0.00000,
    "total": 12,
    "dropShip": false,
    "blanketPO": false,
    "contractManufacturing": true,
    "confirmed": true,
    "closed": false,
    "archived": false,
    "pendingApproval": true,
    "summaryOnly": false,
    "lastSync": "2019-10-22T00:00:00",
    "syncMessage": "",
    "hasSignature": false,
    "lines": [
      {
        "id": 123345,
        "lineNumber": 1,
        "item": {
          "id": 851,
          "name": "Widget"
        },
        "vendorPartNumber": null,
        "class": null,
        "job": null,
        "workcenter": null,
        "tax": {
          "taxable": false,
          "taxCode": {
            "id": 3,
            "name": "NON"
          }
        },
        "linkedTransaction": null,
        "description": null,
        "quantity": 1,
        "weight": 12,
        "volume": 12,
        "weightunit": "lb",
        "volumeunit": "cbm",
        "unitprice": 12,
        "amount": 12,
        "received": 0,
        "uom": {
          "id": 1,
          "name": "each"
        },
        "bin": null,
        "lot": {
          "id": 17,
          "name": "B24XP9381"
        },
        "lotExpiration": null,
        "serials": [
          {
            "id": 837,
            "name": "48147G489"
          },
          {
            "id": 845,
            "name": "48147G498"
          }
        ]
      }
    ],
  }`;

export const ATTRIBUTES = [
  {
    name: "id",
    type: "integer",
    description:
      "Unique identifier for this record. ID field is ignored on create requests.",
  },
  {
    name: "starred",
    type: "integer",
    description: `Indicates if this item receipt has been starred. A value of 0 = no star; 1 or 1-3 = starred. Star colors 
      depend on application configuration. This could be one color of star or three colors of stars. See Company Settings in the user guide for more details.`,
  },
  {
    name: "syncToken",
    type: "integer",
    description: `Indicates the current version of this record. If you receive an error when updating a record, it is because your syncToken is for an older version of the record than that which is currently in the database. Please GET the latest version prior to updating.`,
  },
  {
    name: "number",
    type: "string",
    description: `The order number for this record.  If you wish to use the automatic numbering capability 
    on creation of a item receipt, pass the string “auto”.`,
  },
  {
    name: "date",
    type: "string formatted as YYYY-MM-DDTHH:MM:SS",
    description: `Transaction date.`,
  },
  {
    name: "vendor",
    type: "reference",
    description: "Vendor from which these items were received.",
  },
  {
    name: "location",
    type: "reference",
    description: `Location where these items were stored.`,
  },
  {
    name: "billing",
    type: "transaction address",
    description: `The vendor's billing address.`,
  },
  {
    name: "shipping",
    type: "transaction address",
    description: `The address to which the items should be shipped.`,
  },
  {
    name: "terms",
    type: "reference",
    description: `Payment terms.`,
  },
  {
    name: "department",
    type: "reference",
    description: `Department for this transaction.`,
  },
  {
    name: "assignedToUser",
    type: "reference",
    description: `User to whom this transaction has been assigned.`,
  },
  {
    name: "currency",
    type: "reference",
    description: `Currency used for transaction, if multicurrency is enabled.`,
  },
  {
    name: "taxCode",
    type: "reference",
    description: `Tax code for transaction.`,
  },
  {
    name: "shippingMethod",
    type: "reference",
    description: `Shipping method for transaction.`,
  },
  {
    name: "linkedTransaction",
    type: "linked transaction type",
    description: `The transaction linked to this purchase order. Must be an estimate transaction.`,
  },
  {
    name: "exchangeRate",
    type: "decimal",
    description: `The exchange rate used for this transaction, if multicurrency is enabled.`,
  },
  {
    name: "vendorMessage",
    type: "string",
    description: `Vendor message field.`,
  },
  {
    name: "comment",
    type: "string",
    description:
      "The company’s internal comment about this transaction. This comment is not visible to the vendor.",
  },
  {
    name: "vendorNotes",
    type: "string",
    description: `Vendor notes field. For in-house use.`,
  },
  {
    name: "expectedDate",
    type: "string formatted as YYYY-MM-DDTHH:MM:SS",
    description: `Anticipated date for receiving these items.`,
  },
  {
    name: "expectedShip",
    type: "string formatted as YYYY-MM-DDTHH:MM:SS",
    description: `Anticipated items’ ship date from vendor.`,
  },
  {
    name: "customFields",
    type: "array of custom fields",
    description: `The list of custom fields for this object type.`,
  },
  {
    name: "depositPercent",
    type: "decimal",
    description: `Deposit percentage field.`,
  },
  {
    name: "depositAmount",
    type: "decimal",
    description: `Deposit amount field.`,
  },
  {
    name: "subTotal",
    type: "decimal",
    description: `Subtotal for transaction.`,
    readOnly: true,
  },
  {
    name: "taxAmount",
    type: "decimal",
    description: `Tax on this order.`,
  },
  {
    name: "total",
    type: "decimal",
    description: `Transaction total.`,
    readOnly: true,
  },
  {
    name: "dropShip",
    type: "boolean",
    description: `True if order is to be drop shipped to a customer, false if not.`,
  },
  {
    name: "blanketPO",
    type: "boolean",
    description: `True if transaction is a blanket purchase order, false if not.`,
  },
  {
    name: "contractManufacturing",
    type: "boolean",
    description: `True if order is for contract manufacturing, false if not.`,
  },
  {
    name: "confirmed",
    type: "boolean",
    description: `True if purchase order has been confirmed with the vendor, false if not.`,
  },
  {
    name: "closed",
    type: "boolean",
    description: `True if order is closed, false if not.`,
  },
  {
    name: "archived",
    type: "boolean",
    description: `True if item is archived, false if not.`,
    readOnly: true,
  },
  {
    name: "pendingApproval",
    type: "boolean",
    description: `True if purchase order is awaiting approval, false if not.`,
    readOnly: true,
  },
  {
    name: "summaryOnly",
    type: "boolean",
    description: `Indicates if the summary parameter was set when retrieving back this record.`,
    readOnly: true,
  },
  {
    name: "hasSignature",
    type: "",
    description: `Reserved for future use.`,
    readOnly: true,
  },
  {
    name: "lastSync",
    type: "date",
    newField: "New October 2nd",
    description: `The last successful sync time (GMT) for this purchase order, if syncronizing with Quickbooks.`,
    readOnly: true,
  },
  {
    name: "syncMessage",
    type: "string",
    newField: "New October 2nd",
    description: `The sync error message if the last sync attempt failed with Quickbooks. This will be empty if synchronization is successful.`,
    readOnly: true,
  },
  {
    name: "lines",
    type: "array of purchase order line items",
    description: `The lines for the purchase order.  See object structure below.`,
  },
];

export const LINE_ITEM = [
  {
    name: "id",
    type: "integer",
    description: `The unique identifier for this purchase order line item. ID field is ignored on create requests.`,
  },
  {
    name: "lineNumber",
    type: "integer",
    description: `The line number for this line on the purchase order transaction.`,
  },
  {
    name: "item",
    type: "reference",
    description: `The item this line represents.`,
  },
  {
    name: "vendorPartNumber",
    type: "string",
    description: `Self-explanatory.`,
  },
  {
    name: "class",
    type: "reference",
    description: `The class for this line.`,
  },
  {
    name: "job",
    type: "reference",
    description: `The job for this line, if enabled.`,
  },
  {
    name: "workcenter",
    type: "reference",
    description: `The related work center for the job.`,
  },
  {
    name: "customer",
    type: "reference",
    description: "The customer this line item is targeted for, if any.",
  },
  {
    name: "tax",
    type: "tax information object",
    description: `The tax information for this line, if enabled.`,
  },
  {
    name: "linkedTransaction",
    type: "linked transaction type",
    description: `The transaction linked to this line.`,
  },
  {
    name: "description",
    type: "string",
    description: `The item description.`,
  },
  {
    name: "quantity",
    type: "decimal",
    description: `The quantity of this line item.`,
  },
  {
    name: "weight",
    type: "decimal",
    description: `The weight of this line.`,
    readOnly: true,
  },
  {
    name: "volume",
    type: "decimal",
    description: `The volume of this line.`,
    readOnly: true,
  },
  {
    name: "weightunit",
    type: "string",
    description: `The unit for the item's weight value.`,
    readOnly: true,
  },
  {
    name: "volumeunit",
    type: "string",
    description: `The unit for the item's volume value.`,
    readOnly: true,
  },
  {
    name: "unitPrice",
    type: "decimal",
    description: `The per-unit purchase cost for the item.`,
  },
  {
    name: "amount",
    type: "decimal",
    description: `The purchase amount for this line item. The amount must equal the quantity multiplied by the unit price.`,
  },
  {
    name: "received",
    type: "decimal",
    description: `The quantity of this line item that was received.`,
  },
  {
    name: "uom",
    type: "reference",
    description: `The unit of measure for this line item.`,
  },
  {
    name: "bin",
    type: "reference",
    description: "Unused.",
  },
  {
    name: "lot",
    type: "reference",
    description: "Unused.",
  },
  {
    name: "lotExpiration",
    type: "",
    description: `Unused`,
  },
  {
    name: "serials",
    type: "array of references",
    description: "Unused.",
  },
];

export const QUERY_PARAMS = [
  {
    name: "start",
    type: "number",
    description: `A cursor used in pagination.  This is the row number of the full set of results. 
     The API limits results to a max of 200 results per call.  If you want to retrieve the next set
      of results you can use this parameter to retrive the next set of results.  For example if you
       are retrieving 200 results at a time, you can set start=201 to retrieve the next page of results.`,
  },
  {
    name: "maxresults",
    type: "number",
    description: `The maximum number of results you want to return.  The default is 200, the maximum value allowed.`,
  },
  {
    name: "status",
    type: "string: open/closed",
    description: `Filters results by whether transaction is open or closed.`,
  },
  {
    name: "query",
    type: "string",
    description: `This parameter will filter the results by matches of the string on the following fields: number, comment, vendor name, or customer name.`,
  },
  {
    name: "archived",
    type: "string: yes/no",
    description: `A "yes" returns archived records only; a "no" returns only those that have not been archived.`,
  },
  {
    name: "from/to",
    type: "string formatted as YYYY-MM-DDTHH:MM:SS",
    description: `Returns records based on the beginning and ending transaction dates specified. Both parameters are optional. Using only one parameter allows filtering in one direction. Example:
    from=2019-09-01T00:00:00&to=2019-09-10T00:00:00
   `,
  },
  {
    name: "createdsince/updatedsince",
    type: "string formatted as YYYY-MM-DDTHH:MM:SS ",
    description: `Filters transactions created or updated since a specified date/time.`,
  },
];

export const SAMPLE_QUERY_REQUEST =
  "/api/v2/purchaseorder?start=101&maxresults=100&status=open&from=2019-05-01T00:00:00&to=2019-05-31T00:59:59&createdsince=2019-09-10T00:00:00&updatedsince=2019-09-10T23:59:59";
